import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import { FaceRetouchingNatural as FaceIcon, PhotoCamera as CameraIcon } from '@mui/icons-material';
import { LOGIN_ACCENT } from './loginDarkTheme';

/**
 * Camera capture for face enrollment / login verification.
 * Uses front-facing getUserMedia when available; falls back to file input.
 */
export default function FaceCapture({
  onCapture,
  disabled = false,
  isSubmitting = false,
  variant = 'dark',
  captureLabel = 'Capture face',
  helperText = 'Center your face in good lighting. Only one person in frame.',
}) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError('');
    stopStream();
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('Camera not available. Use the photo upload option.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraReady(true);
    } catch (err) {
      setCameraReady(false);
      setCameraError('Unable to access camera. Allow permission or upload a photo.');
    }
  }, [stopStream]);

  useEffect(() => {
    startCamera();
    return () => {
      stopStream();
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [startCamera, stopStream]);

  const emitBlob = async (blob) => {
    if (!blob || disabled || isSubmitting) {
      return;
    }
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(URL.createObjectURL(blob));
    if (onCapture) {
      await onCapture(blob);
    }
  };

  const captureFromVideo = async () => {
    const video = videoRef.current;
    if (!video || !cameraReady) {
      return;
    }
    const width = video.videoWidth || 640;
    const height = video.videoHeight || 480;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, width, height);
    const blob = await new Promise((resolve) => {
      canvas.toBlob((result) => resolve(result), 'image/jpeg', 0.92);
    });
    await emitBlob(blob);
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    await emitBlob(file);
    event.target.value = '';
  };

  const isDark = variant === 'dark' || variant === 'mobile-dark';
  const frameBorder = isDark ? 'rgba(76, 225, 182, 0.45)' : 'rgba(76, 225, 182, 0.65)';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          position: 'relative',
          borderRadius: '16px',
          overflow: 'hidden',
          border: `2px solid ${frameBorder}`,
          background: isDark ? 'rgba(0,0,0,0.35)' : '#f5f5f5',
          aspectRatio: '4 / 3',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {previewUrl ? (
          <Box
            component="img"
            src={previewUrl}
            alt="Face preview"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Box
            component="video"
            ref={videoRef}
            playsInline
            muted
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transform: 'scaleX(-1)',
            }}
          />
        )}
        {!cameraReady && !previewUrl && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              p: 2,
              textAlign: 'center',
              background: 'rgba(0,0,0,0.55)',
            }}
          >
            <FaceIcon sx={{ color: LOGIN_ACCENT, fontSize: 40 }} />
            <Typography variant="body2" sx={{ color: '#e2e8f0' }}>
              {cameraError || 'Starting camera…'}
            </Typography>
          </Box>
        )}
      </Box>

      <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#546e7a', fontSize: '0.85rem' }}>
        {helperText}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Button
          type="button"
          variant="contained"
          disabled={disabled || isSubmitting || (!cameraReady && !previewUrl)}
          onClick={captureFromVideo}
          startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <CameraIcon />}
          sx={{
            py: 1.25,
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 700,
            background: LOGIN_ACCENT,
            color: '#0a0f1a',
            '&:hover': { background: '#3dd4a8' },
          }}
        >
          {isSubmitting ? 'Verifying…' : captureLabel}
        </Button>
        <Button
          type="button"
          variant="outlined"
          disabled={disabled || isSubmitting}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            py: 1.1,
            borderRadius: '12px',
            textTransform: 'none',
            borderColor: isDark ? 'rgba(255,255,255,0.2)' : '#cfd8dc',
            color: isDark ? '#e2e8f0' : '#37474f',
          }}
        >
          Upload photo instead
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="user"
          hidden
          onChange={handleFileChange}
        />
      </Box>
    </Box>
  );
}
